/**
 * AdminImagePicker — reusable modal that lets an admin browse the Image Manager and pick one asset.
 *
 * Used by: AdminTravelTools.js (Marker Emojis + Phrase Categories) to replace plain emoji/icon
 * inputs with a "click to browse + pick" flow that returns a URL.
 *
 * Props:
 *   open:        boolean — controls visibility
 *   onClose:     () => void — called when the user clicks the X or the backdrop
 *   onPick:      (asset) => void — called with the picked asset object {id, name, url, ...}
 *   API:         backend base URL (string)
 *   authHeaders: { Authorization: 'Bearer ...' } from the calling admin component
 *   title:       optional modal title (default 'Pick an image')
 *
 * Why a separate file: each new feature gets its own module (project preference). This component
 * is intentionally narrow — it does NOT modify or upload anything, just reads from the existing
 * Image Manager endpoints and reports the user's pick back to its parent.
 */
import React, { useEffect, useState } from 'react';

const AdminImagePicker = ({ open, onClose, onPick, API, authHeaders, title = 'Pick an image' }) => {
  const [folders, setFolders] = useState([]);
  const [activeFolderId, setActiveFolderId] = useState(null);
  const [assets, setAssets] = useState([]);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [search, setSearch] = useState('');
  const [error, setError] = useState(null);

  // Image Manager stores asset URLs as RELATIVE paths (e.g., "/api/static/images/x.png").
  // Convert to absolute by prepending the backend origin (API host without "/api" suffix).
  // Full URLs (https://…) are returned as-is so admins can also paste external URLs.
  const ICONS_PSEUDO_FOLDER_ID = '__icons__';
  const apiOrigin = (() => {
    try { return new URL(API, window.location.origin).origin; } catch { return ''; }
  })();
  const toAbsoluteUrl = (raw) => {
    if (!raw) return '';
    if (/^https?:\/\//i.test(raw)) return raw;
    if (raw.startsWith('/')) return `${apiOrigin}${raw}`;
    return raw;
  };

  // Load folders + icon count when the modal opens. We prepend an "Icons" pseudo-folder
  // because the Image Manager stores icons in a separate collection (/admin/icons), not in
  // the regular folder/asset hierarchy.
  useEffect(() => {
    if (!open) return;
    let aborted = false;
    (async () => {
      setLoadingFolders(true); setError(null);
      try {
        // Fetch folders + icons in parallel
        const [foldersRes, iconsRes] = await Promise.all([
          fetch(`${API}/api/admin/image-folders`, { headers: authHeaders }),
          fetch(`${API}/api/admin/icons`, { headers: authHeaders })
        ]);
        if (!foldersRes.ok) throw new Error(`Folders HTTP ${foldersRes.status}`);
        const foldersData = await foldersRes.json();
        const iconsData = iconsRes.ok ? await iconsRes.json() : { icons: [] };
        if (aborted) return;
        const iconCount = (iconsData.icons || []).length;
        // Build display list with Icons pseudo-folder at the top
        const list = [
          { id: ICONS_PSEUDO_FOLDER_ID, name: 'Icons', icon: '🎯', asset_count: iconCount, _isIconsPseudo: true },
          ...(foldersData.folders || [])
        ];
        setFolders(list);
        // Auto-select first folder with assets, else Icons, else first folder
        const firstWithAssets = list.find(f => f.asset_count > 0) || list[0];
        if (firstWithAssets) setActiveFolderId(firstWithAssets.id);
      } catch (e) {
        if (!aborted) setError(String(e.message || e));
      } finally {
        if (!aborted) setLoadingFolders(false);
      }
    })();
    return () => { aborted = true; };
  }, [open, API, authHeaders]);

  // Load assets when active folder changes. Branches on the Icons pseudo-folder.
  useEffect(() => {
    if (!activeFolderId) { setAssets([]); return; }
    let aborted = false;
    (async () => {
      setLoadingAssets(true);
      try {
        const url = activeFolderId === ICONS_PSEUDO_FOLDER_ID
          ? `${API}/api/admin/icons`
          : `${API}/api/admin/image-assets/${activeFolderId}`;
        const res = await fetch(url, { headers: authHeaders });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (aborted) return;
        // Both endpoints return a top-level array under their own key. Normalize.
        const list = data.assets || data.icons || [];
        setAssets(list);
      } catch (e) {
        if (!aborted) setAssets([]);
      } finally {
        if (!aborted) setLoadingAssets(false);
      }
    })();
    return () => { aborted = true; };
  }, [activeFolderId, API, authHeaders]);

  if (!open) return null;

  const filteredAssets = search
    ? assets.filter(a => (a.name || '').toLowerCase().includes(search.toLowerCase()))
    : assets;

  // Helper to pull a usable URL out of varied asset shapes the API may return,
  // and convert it to an absolute URL so it works as an <img src> and as a stored value.
  const assetUrl = (a) => toAbsoluteUrl(a.url || a.image_url || a.file_url || a.path || '');

  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-gray-900 border border-gray-700 rounded-2xl shadow-2xl w-full max-w-5xl max-h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-3 border-b border-gray-700 flex items-center justify-between gap-3 bg-gradient-to-r from-gray-800 to-gray-900">
          <div className="flex items-center gap-3 min-w-0">
            <span className="text-2xl">📁</span>
            <div className="min-w-0">
              <h3 className="text-base font-bold text-white truncate">{title}</h3>
              <p className="text-[11px] text-gray-400">Browse the Image Manager and click an image to pick it</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg bg-gray-700 hover:bg-gray-600 text-white text-sm font-bold flex items-center justify-center" title="Close">✕</button>
        </div>

        {error ? (
          <div className="p-6 text-red-300 bg-red-900/30 border-b border-red-700">
            <div className="font-bold mb-1">Could not load Image Manager</div>
            <div className="text-sm">{error}</div>
          </div>
        ) : null}

        {/* Body — folders sidebar + assets grid */}
        <div className="flex-1 flex min-h-0 overflow-hidden">
          {/* Folders */}
          <div className="w-56 shrink-0 border-r border-gray-700 bg-gray-800/50 overflow-y-auto p-3">
            <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500 px-2 mb-2">Folders</div>
            {loadingFolders ? (
              <div className="text-xs text-gray-400 px-2 py-3">Loading folders…</div>
            ) : folders.length === 0 ? (
              <div className="text-xs text-gray-500 px-2 py-3">No folders yet. Create one in the Image Manager.</div>
            ) : (
              <div className="space-y-1">
                {folders.map(f => (
                  <button
                    key={f.id}
                    onClick={() => setActiveFolderId(f.id)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${
                      activeFolderId === f.id
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-300 hover:bg-gray-700'
                    }`}
                  >
                    <span>{f.icon || '📁'}</span>
                    <span className="truncate flex-1">{f.name}</span>
                    <span className={`text-[10px] font-bold ${activeFolderId === f.id ? 'text-blue-100' : 'text-gray-500'}`}>{f.asset_count || 0}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Assets grid */}
          <div className="flex-1 flex flex-col min-w-0">
            {/* Search */}
            <div className="px-4 py-2 border-b border-gray-700 bg-gray-800/30">
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filter by name…"
                className="w-full px-3 py-1.5 rounded-lg bg-gray-700 border border-gray-600 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            {/* Grid */}
            <div className="flex-1 overflow-y-auto p-4">
              {loadingAssets ? (
                <div className="text-center text-gray-400 py-12">Loading images…</div>
              ) : filteredAssets.length === 0 ? (
                <div className="text-center text-gray-500 py-12">
                  {search ? 'No images match your filter.' : 'This folder is empty. Upload images in the Image Manager first.'}
                </div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
                  {filteredAssets.map(a => {
                    const url = assetUrl(a);
                    return (
                      <button
                        key={a.id}
                        onClick={() => { onPick({ ...a, url }); onClose(); }}
                        className="group relative aspect-square rounded-lg overflow-hidden bg-gray-700 hover:ring-2 hover:ring-blue-500 transition-all"
                        title={`${a.name || ''}\nClick to pick`}
                      >
                        {url ? (
                          <img src={url} alt={a.name || ''} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-500 text-3xl">🖼️</div>
                        )}
                        {a.name && (
                          <div className="absolute inset-x-0 bottom-0 px-1.5 py-1 bg-black/60 text-white text-[10px] font-bold truncate opacity-0 group-hover:opacity-100 transition-opacity">
                            {a.name}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-2 border-t border-gray-700 bg-gray-800 text-[11px] text-gray-400">
          Tip: click any image to pick it. The chosen URL will be saved to the database.
        </div>
      </div>
    </div>
  );
};

export default AdminImagePicker;
